import React from 'react';
import _ from 'lodash';
import { round } from '../../../utils/formatter';
import * as part1Config from './part1';
import * as part2Config from './part2';
import * as part3Config from './part3';
import * as part5Config from './part5';
import { parseMonetaryValue } from 'utils/parsers';
import type { UseFormReturn } from 'react-hook-form';

const allFields = [];
const formConfig = [part1Config, part2Config, part3Config, part5Config];
formConfig.forEach((pageConfig, pageIndex) => {
  const { data: pageData, generateHeaders } = pageConfig;
  const headers = generateHeaders();
  pageData.forEach((section, sectionIndex) => {
    section.rows.forEach((row, rowIndex) => {
      headers.forEach((header) => {
        const key = header.key;
        const keyParts = [pageIndex, sectionIndex, rowIndex, key];
        const name = keyParts.join('-');
        allFields.push({
          name,
          key,
          pageIndex,
          sectionIndex,
          rowIndex,
          sectionName: section.header,
          rowLabel: row.label,
          fieldLabel: header.label,
        });
      });
    });
  });
});

export const useCalculationGraph = (): Map<
  string,
  typeof fieldCalculations,
> => {
  return React.useMemo(() => {
    const calculationGraph = new Map();

    for (const fieldCalculation of fieldCalculations) {
      if (!fieldCalculation.watchFields?.length) {
        continue;
      }

      for (const field of fieldCalculation.watchFields) {
        if (!calculationGraph.has(field)) {
          calculationGraph.set(field, []);
        }

        calculationGraph.get(field).push(fieldCalculation);
      }
    }

    return calculationGraph;
  }, []);
};

export const useAutoCalculations = (
  useFormReturn: UseFormReturn,
  calculateIntacctDifference,
) => {
  const calculationGraph = useCalculationGraph();
  const syncRef = React.useRef(new Set());
  // The Rental Income and rate a Build Budget record's saved Vacancy was loaded with (FES-1813)
  const savedVacancyInputsRef = React.useRef(null);

  const recalculate = (isInitial: boolean = false) => {
    const uniqueDependencies = new Set();

    calculationGraph.forEach((graph) => {
      uniqueDependencies.add(graph);
    });

    uniqueDependencies.forEach((dependencies) => {
      dependencies.forEach((dependency) =>
        dependency.onChange({
          useFormReturn,
          syncSet: syncRef.current,
          savedVacancyInputs: savedVacancyInputsRef,
          isInitial,
          triggerType: 'auto',
          ...dependency,
        }),
      );
    });
  };

  React.useEffect(() => {
    const pending = new Set();
    let flushing = false;

    function flush() {
      if (flushing) {
        return;
      }

      flushing = true;

      queueMicrotask(() => {
        while (pending.size) {
          const current = [...pending];

          pending.clear();

          current.forEach((calculationGraph) => {
            calculationGraph.onChange({
              useFormReturn,
              isInitial: false,
              syncSet: syncRef.current,
              savedVacancyInputs: savedVacancyInputsRef,
              ...calculationGraph,
            });
          });
        }

        flushing = false;
      });
    }

    return useFormReturn.subscribe({
      formState: { values: true },
      callback: ({ name, values }) => {
        calculateIntacctDifference(
          name,
          values[name],
          ignoreDifferenceFields.has(name),
        );

        calculationGraph.get(name)?.forEach((fieldCalculation) => {
          pending.add(fieldCalculation);
        });

        flush();
      },
    });
  }, []);

  return {
    calculationGraph,
    recalculate,
    savedVacancyInputsRef,
  };
};

function linkFields({
  useFormReturn,
  watchFields,
  destinationField,
  syncSet,
  isInitial,
}) {
  // Do nothing on data load
  if (isInitial) {
    return;
  }

  const currentValue = useFormReturn.getValues(watchFields[0]);

  // Values have been synced, no need to continue. Remove their footprint in sync
  if (syncSet.has(watchFields[0])) {
    syncSet.delete(watchFields[0]);
    syncSet.delete(destinationField);

    return;
  }

  // Add footprint changes to sync set so we can skip if they are already tracked
  syncSet.add(watchFields[0]);
  syncSet.add(destinationField);

  useFormReturn.setValue(destinationField, currentValue, {
    shouldDirty: true,
    shouldValidate: false,
    shouldTouch: false,
  });
}

function sumFields({
  useFormReturn,
  watchFields,
  destinationField,
  manualTriggerOnly,
  triggerType,
}) {
  const values = useFormReturn.getValues(watchFields);
  let sum = 0;

  if (!values || (manualTriggerOnly === true && triggerType !== 'manual')) {
    return;
  }

  // for-loop is faster than reduce
  for (let i = 0; i < values.length; i++) {
    sum += parseMonetaryValue(values[i]);
  }

  const formattedSum = (Math.round(sum * 100) / 100).toFixed(2);

  const currentValue = useFormReturn.getValues(destinationField);

  if (currentValue !== formattedSum) {
    useFormReturn.setValue(destinationField, formattedSum, {
      shouldDirty: true,
      shouldValidate: false,
      shouldTouch: false,
    });
  }
}

const sumFieldsWithDeductionLines = ({
  useFormReturn,
  watchFields,
  destinationField,
}) => {
  const values = useFormReturn.getValues(watchFields);

  const sum1to7 = values
    .slice(0, 7)
    .reduce((acc, value) => acc + parseMonetaryValue(value || '0'), 0);
  const sum8to9 = values
    .slice(7, 9)
    .reduce(
      (acc, value) => acc + Math.abs(parseMonetaryValue(value || '0')),
      0,
    );

  useFormReturn.setValue(
    destinationField,
    (Math.round((sum1to7 - sum8to9) * 100) / 100).toFixed(2),
  );
};

function calculateFields({
  useFormReturn,
  watchFields,
  destinationField,
  operator,
}) {
  const values = useFormReturn.getValues(watchFields);

  if (!values) return;

  const parsed = values.map((v) => parseMonetaryValue(v));

  let result = parsed[0] ?? 0;

  // This looks a bit ugly but this ensures decision happens once
  switch (operator) {
    case '+':
      for (let i = 1; i < parsed.length; i++) {
        result += parsed[i];
      }
      break;
    case '-':
      for (let i = 1; i < parsed.length; i++) {
        result -= parsed[i];
      }
      break;
    default:
      // Do nothing, specified operator isn't supported
      return;
  }

  const formatted = (Math.round(result * 100) / 100).toFixed(2);

  useFormReturn.setValue(destinationField, formatted, {
    shouldDirty: true,
    shouldValidate: false,
    shouldTouch: false,
  });
}

function calculateEndingBalance({
  useFormReturn,
  watchFields,
  destinationField,
  totalAbs,
}) {
  const values = useFormReturn.getValues(watchFields);

  const [beginningBalance, transferToReserve, total] = values.map((value) =>
    parseMonetaryValue(value || 0),
  );
  const result =
    beginningBalance + transferToReserve - (totalAbs ? Math.abs(total) : total);
  const formattedResult = (Math.round(result * 100) / 100).toFixed(2);

  useFormReturn.setValue(destinationField, formattedResult, {
    shouldDirty: true,
    shouldValidate: false,
    shouldTouch: false,
  });
}

const createPart5Fields = (key) => {
  return [
    `3-0-0-${key}`,
    `3-0-1-${key}`,
    `3-0-2-${key}`,
    `3-0-3-${key}`,
    `3-0-4-${key}`,
    `3-1-0-${key}`,
    `3-1-1-${key}`,
    `3-1-2-${key}`,
    `3-1-3-${key}`,
    `3-1-4-${key}`,
    `3-2-0-${key}`,
    `3-2-1-${key}`,
    `3-2-2-${key}`,
    `3-3-0-${key}`,
    `3-3-1-${key}`,
    `3-3-2-${key}`,
    `3-4-0-${key}`,
    `3-4-1-${key}`,
    `3-5-0-${key}`,
    `3-5-1-${key}`,
    `3-5-2-${key}`,
    `3-6-0-${key}`,
    `3-6-1-${key}`,
    `3-6-2-${key}`,
    `3-6-3-${key}`,
    `3-6-4-${key}`,
    `3-6-5-${key}`,
    `3-7-0-${key}`,
    `3-7-1-${key}`,
    `3-8-0-${key}`,
    `3-8-1-${key}`,
    `3-8-2-${key}`,
    `3-8-3-${key}`,
    `3-8-4-${key}`,
    `3-8-5-${key}`,
    `3-8-6-${key}`,
    `3-9-0-${key}`,
    `3-9-1-${key}`,
    `3-9-2-${key}`,
    `3-9-3-${key}`,
    `3-10-0-${key}`,
    `3-10-1-${key}`,
    `3-10-2-${key}`,
    `3-10-3-${key}`,
    `3-10-4-${key}`,
    `3-10-5-${key}`,
    `3-11-0-${key}`,
    `3-11-1-${key}`,
    `3-12-0-${key}`,
    `3-12-1-${key}`,
    `3-12-2-${key}`,
    `3-13-0-${key}`,
    `3-13-1-${key}`,
    `3-13-2-${key}`,
  ];
};

function calculateVariance({ useFormReturn, watchFields, destinationField }) {
  const [current, proposed] = useFormReturn
    .getValues(watchFields)
    .map(parseMonetaryValue);
  const currentVariance = useFormReturn.getValues(destinationField);
  let result = 0.0;

  if (proposed === 0) {
    result = current !== 0 ? 100.0 : 0.0;
  } else {
    result = round(((proposed - current) / current) * 100);
  }

  if (currentVariance !== result) {
    useFormReturn.setValue(destinationField, result, {
      shouldDirty: true,
      shouldValidate: false,
      shouldTouch: false,
    });
  }
}

const expenseKeys = [
  'priceCurrent',
  'unitsProposed',
  'reserveProposed',
  'reserveActual',
  'operatingProposed',
  'operatingActual',
  'totalActual',
  'unitsActual',
];

function expenseCalculations() {
  return expenseKeys.map((key) => ({
    watchFields: createPart5Fields(key),
    destinationField: `3-14-0-${key}`,
    disableFields: [`3-14-0-${key}`],
    onChange: sumFields,
  }));
}

function createReserveProposed() {
  return createPart5Fields('priceCurrent').map((key) => {
    const reserveProposedKey = key.replace('priceCurrent', 'reserveProposed');
    const unitsProposedKey = key.replace('priceCurrent', 'unitsProposed');
    const operatingProposedKey = key.replace(
      'priceCurrent',
      'operatingProposed',
    );

    return {
      watchFields: [key, unitsProposedKey, operatingProposedKey],
      destinationField: reserveProposedKey,
      disableFields: [reserveProposedKey],
      onChange: ({ useFormReturn, watchFields, destinationField }) => {
        const currentReserve = parseMonetaryValue(
          useFormReturn.getValues(destinationField) || 0,
        );
        const price = parseMonetaryValue(
          useFormReturn.getValues(watchFields[0]) || 0,
        );
        const units = parseMonetaryValue(
          useFormReturn.getValues(watchFields[1]) || 0,
        );
        const operating = parseMonetaryValue(
          useFormReturn.getValues(watchFields[2]) || 0,
        );

        const reserveProposed = price * units - operating;

        if (Math.abs(reserveProposed - currentReserve) < 0.1) {
          return;
        }

        const formattedReserve = (
          Math.round(reserveProposed * 100) / 100
        ).toFixed(2);

        requestAnimationFrame(() => {
          useFormReturn.setValue(reserveProposedKey, formattedReserve, {
            shouldDirty: true,
            shouldValidate: false,
            shouldTouch: false,
          });
        });
      },
    };
  });
}

function calculateP5TotalActual({
  useFormReturn,
  watchFields,
  destinationField,
}) {
  const values = useFormReturn.getValues(watchFields);

  if (values.some((value) => _.isNil(value))) {
    useFormReturn.setValue(destinationField, '—');
    return;
  }

  const [reserveActual, operatingActual, price] = values.map((value) =>
    parseMonetaryValue(value || 0),
  );

  const sum = (reserveActual + operatingActual) / price;
  if (!isFinite(sum)) {
    // NaN or Infinity
    useFormReturn.setValue(destinationField, '—');
    return;
  }
  const formattedSum = Math.ceil(sum).toFixed(0);
  useFormReturn.setValue(destinationField, formattedSum);
}

function calculateVacancy({
  useFormReturn,
  watchFields,
  destinationField,
  savedVacancyInputs,
}) {
  const baseValue = parseMonetaryValue(
    useFormReturn.getValues(watchFields[0]) || 0, // proposedBudget
  );
  const comments = useFormReturn.getValues(watchFields[1]); // comments

  // Keep a saved Vacancy until Rental Income or the rate really changes: loading the
  // budget also reports both fields as changed (FES-1813)
  if (savedVacancyInputs.current) {
    const { rentalIncome, rate } = savedVacancyInputs.current;

    if (
      parseMonetaryValue(rentalIncome || 0) === baseValue &&
      Object.is(parseFloat(rate), parseFloat(comments))
    ) {
      return;
    }

    savedVacancyInputs.current = null;
  }

  if (!baseValue || baseValue <= 0) {
    return;
  }

  let percentage = 0;
  if (comments) {
    if (comments) {
      const cleanComment = comments.replace('%', '').trim();
      percentage = parseFloat(cleanComment);
    }
  }

  if (!isNaN(percentage)) {
    const calculatedValue =
      Math.round(baseValue * (percentage / 100) * 100) / 100;
    const currentProposed = parseMonetaryValue(
      useFormReturn.getValues(destinationField) || 0,
    );

    if (Math.abs(calculatedValue - currentProposed) > 0.01) {
      const result = calculatedValue.toFixed(2);

      useFormReturn.setValue(destinationField, result, {
        shouldDirty: true,
        shouldValidate: false,
        shouldTouch: false,
      });
    }
  }
}

export const fieldCalculations = [
  // PART I
  {
    // 1. RENTAL INCOME
    disableFields: ['0-0-0-proposedBudget'],
  },
  {
    // 8. LESS (VACANCY)
    disableFields: ['0-0-7-proposedBudget', '0-0-7-comments'],
  },
  {
    // 10. SUB-TOTAL
    watchFields: [
      '0-0-0-proposedBudget',
      '0-0-1-proposedBudget',
      '0-0-2-proposedBudget',
      '0-0-3-proposedBudget',
      '0-0-4-proposedBudget',
      '0-0-5-proposedBudget',
      '0-0-6-proposedBudget',
      '0-0-7-proposedBudget',
      '0-0-8-proposedBudget',
    ],
    destinationField: '0-0-9-proposedBudget',
    disableFields: ['0-0-9-proposedBudget'],
    onChange: sumFieldsWithDeductionLines,
  },
  {
    watchFields: [
      '0-0-0-actual',
      '0-0-1-actual',
      '0-0-2-actual',
      '0-0-3-actual',
      '0-0-4-actual',
      '0-0-5-actual',
      '0-0-6-actual',
      '0-0-7-actual',
      '0-0-8-actual',
    ],
    destinationField: '0-0-9-actual',
    disableFields: ['0-0-9-actual'],
    onChange: sumFieldsWithDeductionLines,
  },
  {
    watchFields: [
      '0-0-0-currentBudget',
      '0-0-1-currentBudget',
      '0-0-2-currentBudget',
      '0-0-3-currentBudget',
      '0-0-4-currentBudget',
      '0-0-5-currentBudget',
      '0-0-6-currentBudget',
      '0-0-7-currentBudget',
      '0-0-8-currentBudget',
    ],
    destinationField: '0-0-9-currentBudget',
    disableFields: ['0-0-9-currentBudget'],
    onChange: sumFieldsWithDeductionLines,
  },
  {
    // 13. Transfer from Reserve
    watchFields: ['2-0-7-currentBudget'],
    destinationField: '0-1-2-currentBudget',
    onChange: sumFields,
  },
  {
    // 13. Transfer from Reserve
    watchFields: ['2-0-7-actual'],
    destinationField: '0-1-2-actual',
    onChange: sumFields,
  },
  {
    // 14. SUB-TOTAL
    watchFields: [
      '0-1-0-proposedBudget',
      '0-1-1-proposedBudget',
      '0-1-2-proposedBudget',
    ],
    destinationField: '0-1-3-proposedBudget',
    disableFields: ['0-1-3-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: ['0-1-0-actual', '0-1-1-actual', '0-1-2-actual'],
    destinationField: '0-1-3-actual',
    disableFields: ['0-1-3-actual'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '0-1-0-currentBudget',
      '0-1-1-currentBudget',
      '0-1-2-currentBudget',
    ],
    destinationField: '0-1-3-currentBudget',
    disableFields: ['0-1-3-currentBudget'],
    onChange: sumFields,
  },
  {
    // 15. TOTAL CASH SOURCES
    watchFields: ['0-0-9-proposedBudget', '0-1-3-proposedBudget'],
    destinationField: '0-1-4-proposedBudget',
    disableFields: ['0-1-4-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: ['0-0-9-actual', '0-1-3-actual'],
    destinationField: '0-1-4-actual',
    disableFields: ['0-1-4-actual'],
    onChange: sumFields,
  },
  {
    watchFields: ['0-0-9-currentBudget', '0-1-3-currentBudget'],
    destinationField: '0-1-4-currentBudget',
    disableFields: ['0-1-4-currentBudget'],
    onChange: sumFields,
  },
  {
    // 13. TRANSFER FROM RESERVE
    watchFields: [
      '2-0-4-proposedBudget',
      '2-0-5-proposedBudget',
      '2-0-6-proposedBudget',
    ],
    destinationField: '0-1-2-proposedBudget',
    disableFields: ['0-1-2-proposedBudget'],
    onChange: sumFields,
  },
  {
    // 16. TOTAL O&M EXPENSES
    // It was previously using linkFields function but sumFields can just achieve the same result
    watchFields: ['1-3-7-proposedBudget'],
    destinationField: '0-2-0-proposedBudget',
    disableFields: ['0-2-0-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: ['1-3-7-actual'],
    destinationField: '0-2-0-actual',
    disableFields: ['0-2-0-actual'],
    onChange: sumFields,
  },
  {
    watchFields: ['1-3-7-currentBudget'],
    destinationField: '0-2-0-currentBudget',
    disableFields: ['0-2-0-currentBudget'],
    onChange: sumFields,
  },
  {
    // 17. RHS Debt Payment
    watchFields: ['0-2-1-currentBudget'],
    destinationField: '0-2-1-proposedBudget',
    manualTriggerOnly: true,
    onChange: sumFields,
  },
  {
    // 22. TRANSFER TO RESERVE
    watchFields: ['0-2-6-currentBudget'],
    destinationField: '0-2-6-proposedBudget',
    manualTriggerOnly: true,
    onChange: sumFields,
  },
  {
    watchFields: ['2-0-1-proposedBudget'],
    destinationField: '0-2-6-proposedBudget',
    onChange: linkFields,
  },
  {
    // 23. RTO
    watchFields: ['0-2-7-currentBudget'],
    destinationField: '0-2-7-proposedBudget',
    manualTriggerOnly: true,
    onChange: sumFields,
  },
  {
    // 24. SUB-TOTAL
    watchFields: [
      '0-2-0-proposedBudget',
      '0-2-1-proposedBudget',
      '0-2-2-proposedBudget',
      '0-2-3-proposedBudget',
      '0-2-4-proposedBudget',
      '0-2-5-proposedBudget',
      '0-2-6-proposedBudget',
      '0-2-7-proposedBudget',
    ],
    destinationField: '0-2-8-proposedBudget',
    disableFields: ['0-2-8-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '0-2-0-actual',
      '0-2-1-actual',
      '0-2-2-actual',
      '0-2-3-actual',
      '0-2-4-actual',
      '0-2-5-actual',
      '0-2-6-actual',
      '0-2-7-actual',
    ],
    destinationField: '0-2-8-actual',
    disableFields: ['0-2-8-actual'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '0-2-0-currentBudget',
      '0-2-1-currentBudget',
      '0-2-2-currentBudget',
      '0-2-3-currentBudget',
      '0-2-4-currentBudget',
      '0-2-5-currentBudget',
      '0-2-6-currentBudget',
      '0-2-7-currentBudget',
    ],
    destinationField: '0-2-8-currentBudget',
    disableFields: ['0-2-8-currentBudget'],
    onChange: sumFields,
  },
  // 25. NON OPERATIONAL
  {
    watchFields: ['0-3-0-currentBudget'],
    destinationField: '0-3-0-proposedBudget',
    manualTriggerOnly: true,
    onChange: sumFields,
  },
  {
    // 26. ANNUAL CAPITAL BUDGET
    watchFields: [
      '2-0-4-proposedBudget',
      '2-0-5-proposedBudget',
      '2-0-6-proposedBudget',
    ],
    destinationField: '0-3-1-proposedBudget',
    disableFields: ['0-3-1-proposedBudget'],
    onChange: sumFields,
  },
  {
    // 26. ANNUAL CAPITAL BUDGET
    watchFields: ['2-0-4-actual', '2-0-5-actual', '2-0-6-actual'],
    destinationField: '0-3-1-actual',
    onChange: sumFields,
  },
  {
    // 26. ANNUAL CAPITAL BUDGET
    watchFields: [
      '2-0-4-currentBudget',
      '2-0-5-currentBudget',
      '2-0-6-currentBudget',
    ],
    destinationField: '0-3-1-currentBudget',
    onChange: sumFields,
  },
  // 27. MISCELLANEOUS
  {
    watchFields: ['0-3-2-currentBudget'],
    destinationField: '0-3-2-proposedBudget',
    manualTriggerOnly: true,
    onChange: sumFields,
  },
  {
    // 28. SUB-TOTAL
    watchFields: [
      '0-3-0-proposedBudget',
      '0-3-1-proposedBudget',
      '0-3-2-proposedBudget',
    ],
    destinationField: '0-3-3-proposedBudget',
    disableFields: ['0-3-3-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: ['0-3-0-actual', '0-3-1-actual', '0-3-2-actual'],
    destinationField: '0-3-3-actual',
    disableFields: ['0-3-3-actual'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '0-3-0-currentBudget',
      '0-3-1-currentBudget',
      '0-3-2-currentBudget',
    ],
    destinationField: '0-3-3-currentBudget',
    disableFields: ['0-3-3-currentBudget'],
    onChange: sumFields,
  },
  {
    // 29. TOTAL CASH USES
    watchFields: ['0-2-8-proposedBudget', '0-3-3-proposedBudget'],
    destinationField: '0-3-4-proposedBudget',
    disableFields: ['0-3-4-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: ['0-2-8-actual', '0-3-3-actual'],
    destinationField: '0-3-4-actual',
    disableFields: ['0-3-4-actual'],
    onChange: sumFields,
  },
  {
    watchFields: ['0-2-8-currentBudget', '0-3-3-currentBudget'],
    destinationField: '0-3-4-currentBudget',
    disableFields: ['0-3-4-currentBudget'],
    onChange: sumFields,
  },
  {
    // 30. NET CASH (DEFICIT)
    watchFields: ['0-1-4-proposedBudget', '0-3-4-proposedBudget'],
    destinationField: '0-3-5-proposedBudget',
    disableFields: ['0-3-5-proposedBudget'],
    operator: '-',
    onChange: calculateFields,
  },
  {
    watchFields: ['2-1-0-actual', '2-2-0-actual'],
    destinationField: '0-4-0-actual',
    disableFields: ['0-4-0-actual'],
    onChange: sumFields,
  },
  {
    watchFields: ['0-1-4-actual', '0-3-4-actual'],
    destinationField: '0-3-5-actual',
    disableFields: ['0-3-5-actual'],
    operator: '-',
    onChange: calculateFields,
  },
  {
    watchFields: ['0-1-4-currentBudget', '0-3-4-currentBudget'],
    destinationField: '0-3-5-currentBudget',
    disableFields: ['0-3-5-currentBudget'],
    operator: '-',
    onChange: calculateFields,
  },
  {
    // 33. ENDING CASH BALANCE
    watchFields: [
      '0-3-5-proposedBudget',
      '0-4-0-proposedBudget',
      '0-4-1-proposedBudget',
    ],
    destinationField: '0-4-2-proposedBudget',
    disableFields: ['0-4-2-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: ['0-3-5-actual', '0-4-0-actual', '0-4-1-actual'],
    destinationField: '0-4-2-actual',
    disableFields: ['0-4-2-actual'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '0-3-5-currentBudget',
      '0-4-0-currentBudget',
      '0-4-1-currentBudget',
    ],
    destinationField: '0-4-2-currentBudget',
    disableFields: ['0-4-2-currentBudget'],
    onChange: sumFields,
  },
  // Variance lines
  {
    // Line 11: Sub-Total Maint. and Operating
    watchFields: ['1-0-10-currentBudget', '1-0-10-proposedBudget'],
    destinationField: '1-0-10-variancePercentage',
    disableFields: ['1-0-10-variancePercentage'],
    onChange: calculateVariance,
  },
  {
    // Line 18: Sub-Total Utilities
    watchFields: ['1-1-6-currentBudget', '1-1-6-proposedBudget'],
    destinationField: '1-1-6-variancePercentage',
    disableFields: ['1-1-6-variancePercentage'],
    onChange: calculateVariance,
  },
  {
    // Line 33: Sub-Total Administrative
    watchFields: ['1-2-14-currentBudget', '1-2-14-proposedBudget'],
    destinationField: '1-2-14-variancePercentage',
    disableFields: ['1-2-14-variancePercentage'],
    onChange: calculateVariance,
  },
  {
    // Line 40: Sub-Total Taxes and Insurance
    watchFields: ['1-3-6-currentBudget', '1-3-6-proposedBudget'],
    destinationField: '1-3-6-variancePercentage',
    disableFields: ['1-3-6-variancePercentage'],
    onChange: calculateVariance,
  },
  {
    // 9. ANNUAL CAPITAL BUDGET
    watchFields: [
      '3-0-0-operatingProposed',
      '3-0-1-operatingProposed',
      '3-0-2-operatingProposed',
      '3-0-3-operatingProposed',
      '3-0-4-operatingProposed',
      '3-1-0-operatingProposed',
      '3-1-1-operatingProposed',
      '3-1-2-operatingProposed',
      '3-1-3-operatingProposed',
      '3-1-4-operatingProposed',
      '3-2-0-operatingProposed',
      '3-2-1-operatingProposed',
      '3-2-2-operatingProposed',
      '3-3-0-operatingProposed',
      '3-3-1-operatingProposed',
      '3-3-2-operatingProposed',
      '3-4-0-operatingProposed',
      '3-4-1-operatingProposed',
      '3-5-0-operatingProposed',
      '3-5-1-operatingProposed',
      '3-5-2-operatingProposed',
      '3-6-0-operatingProposed',
      '3-6-1-operatingProposed',
      '3-6-2-operatingProposed',
      '3-6-3-operatingProposed',
      '3-6-4-operatingProposed',
      '3-6-5-operatingProposed',
      '3-7-0-operatingProposed',
      '3-7-1-operatingProposed',
      '3-8-0-operatingProposed',
      '3-8-1-operatingProposed',
      '3-8-2-operatingProposed',
      '3-8-3-operatingProposed',
      '3-8-4-operatingProposed',
      '3-8-5-operatingProposed',
      '3-8-6-operatingProposed',
      '3-9-0-operatingProposed',
      '3-9-1-operatingProposed',
      '3-9-2-operatingProposed',
      '3-9-3-operatingProposed',
      '3-10-0-operatingProposed',
      '3-10-1-operatingProposed',
      '3-10-2-operatingProposed',
      '3-10-3-operatingProposed',
      '3-10-4-operatingProposed',
      '3-10-5-operatingProposed',
      '3-11-0-operatingProposed',
      '3-11-1-operatingProposed',
      '3-12-0-operatingProposed',
      '3-12-1-operatingProposed',
      '3-12-2-operatingProposed',
      '3-13-0-operatingProposed',
      '3-13-1-operatingProposed',
      '3-13-2-operatingProposed',
    ],
    destinationField: '1-0-8-proposedBudget',
    disableFields: ['1-0-8-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '3-0-0-operatingActual',
      '3-0-1-operatingActual',
      '3-0-2-operatingActual',
      '3-0-3-operatingActual',
      '3-0-4-operatingActual',
      '3-1-0-operatingActual',
      '3-1-1-operatingActual',
      '3-1-2-operatingActual',
      '3-1-3-operatingActual',
      '3-1-4-operatingActual',
      '3-2-0-operatingActual',
      '3-2-1-operatingActual',
      '3-2-2-operatingActual',
      '3-3-0-operatingActual',
      '3-3-1-operatingActual',
      '3-3-2-operatingActual',
      '3-4-0-operatingActual',
      '3-4-1-operatingActual',
      '3-5-0-operatingActual',
      '3-5-1-operatingActual',
      '3-5-2-operatingActual',
      '3-6-0-operatingActual',
      '3-6-1-operatingActual',
      '3-6-2-operatingActual',
      '3-6-3-operatingActual',
      '3-6-4-operatingActual',
      '3-6-5-operatingActual',
      '3-7-0-operatingActual',
      '3-7-1-operatingActual',
      '3-8-0-operatingActual',
      '3-8-1-operatingActual',
      '3-8-2-operatingActual',
      '3-8-3-operatingActual',
      '3-8-4-operatingActual',
      '3-8-5-operatingActual',
      '3-8-6-operatingActual',
      '3-9-0-operatingActual',
      '3-9-1-operatingActual',
      '3-9-2-operatingActual',
      '3-9-3-operatingActual',
      '3-10-0-operatingActual',
      '3-10-1-operatingActual',
      '3-10-2-operatingActual',
      '3-10-3-operatingActual',
      '3-10-4-operatingActual',
      '3-10-5-operatingActual',
      '3-11-0-operatingActual',
      '3-11-1-operatingActual',
      '3-12-0-operatingActual',
      '3-12-1-operatingActual',
      '3-12-2-operatingActual',
      '3-13-0-operatingActual',
      '3-13-1-operatingActual',
      '3-13-2-operatingActual',
    ],
    destinationField: '1-0-8-actual',
    disableFields: ['1-0-8-actual'],
    onChange: sumFields,
  },
  {
    // 11 SUB-TOTAL
    watchFields: [
      '1-0-0-proposedBudget',
      '1-0-1-proposedBudget',
      '1-0-2-proposedBudget',
      '1-0-3-proposedBudget',
      '1-0-4-proposedBudget',
      '1-0-5-proposedBudget',
      '1-0-6-proposedBudget',
      '1-0-7-proposedBudget',
      '1-0-8-proposedBudget',
      '1-0-9-proposedBudget',
    ],
    destinationField: '1-0-10-proposedBudget',
    disableFields: ['1-0-10-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '1-0-0-actual',
      '1-0-1-actual',
      '1-0-2-actual',
      '1-0-3-actual',
      '1-0-4-actual',
      '1-0-5-actual',
      '1-0-6-actual',
      '1-0-7-actual',
      '1-0-8-actual',
      '1-0-9-actual',
    ],
    destinationField: '1-0-10-actual',
    disableFields: ['1-0-10-actual'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '1-0-0-currentBudget',
      '1-0-1-currentBudget',
      '1-0-2-currentBudget',
      '1-0-3-currentBudget',
      '1-0-4-currentBudget',
      '1-0-5-currentBudget',
      '1-0-6-currentBudget',
      '1-0-7-currentBudget',
      '1-0-8-currentBudget',
      '1-0-9-currentBudget',
    ],
    destinationField: '1-0-10-currentBudget',
    disableFields: ['1-0-10-currentBudget'],
    onChange: sumFields,
  },
  {
    // 18. SUB-TOTAL UTILITIES
    watchFields: [
      '1-1-0-proposedBudget',
      '1-1-1-proposedBudget',
      '1-1-2-proposedBudget',
      '1-1-3-proposedBudget',
      '1-1-4-proposedBudget',
      '1-1-5-proposedBudget',
    ],
    destinationField: '1-1-6-proposedBudget',
    disableFields: ['1-1-6-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '1-1-0-actual',
      '1-1-1-actual',
      '1-1-2-actual',
      '1-1-3-actual',
      '1-1-4-actual',
      '1-1-5-actual',
    ],
    destinationField: '1-1-6-actual',
    disableFields: ['1-1-6-actual'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '1-1-0-currentBudget',
      '1-1-1-currentBudget',
      '1-1-2-currentBudget',
      '1-1-3-currentBudget',
      '1-1-4-currentBudget',
      '1-1-5-currentBudget',
    ],
    destinationField: '1-1-6-currentBudget',
    disableFields: ['1-1-6-currentBudget'],
    onChange: sumFields,
  },
  {
    // 20. MANAGEMENT FEE
    disableFields: ['1-2-1-proposedBudget', '1-2-1-comments'],
  },
  {
    // 33. SUB-TOTAL ADMINISTRATIVE
    watchFields: [
      '1-2-0-proposedBudget',
      '1-2-1-proposedBudget',
      '1-2-2-proposedBudget',
      '1-2-3-proposedBudget',
      '1-2-4-proposedBudget',
      '1-2-5-proposedBudget',
      '1-2-6-proposedBudget',
      '1-2-7-proposedBudget',
      '1-2-8-proposedBudget',
      '1-2-9-proposedBudget',
      '1-2-10-proposedBudget',
      '1-2-11-proposedBudget',
      '1-2-12-proposedBudget',
      '1-2-13-proposedBudget',
    ],
    destinationField: '1-2-14-proposedBudget',
    disableFields: ['1-2-14-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '1-2-0-actual',
      '1-2-1-actual',
      '1-2-2-actual',
      '1-2-3-actual',
      '1-2-4-actual',
      '1-2-5-actual',
      '1-2-6-actual',
      '1-2-7-actual',
      '1-2-8-actual',
      '1-2-9-actual',
      '1-2-10-actual',
      '1-2-11-actual',
      '1-2-12-actual',
      '1-2-13-actual',
    ],
    destinationField: '1-2-14-actual',
    disableFields: ['1-2-14-actual'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '1-2-0-currentBudget',
      '1-2-1-currentBudget',
      '1-2-2-currentBudget',
      '1-2-3-currentBudget',
      '1-2-4-currentBudget',
      '1-2-5-currentBudget',
      '1-2-6-currentBudget',
      '1-2-7-currentBudget',
      '1-2-8-currentBudget',
      '1-2-9-currentBudget',
      '1-2-10-currentBudget',
      '1-2-11-currentBudget',
      '1-2-12-currentBudget',
      '1-2-13-currentBudget',
    ],
    destinationField: '1-2-14-currentBudget',
    disableFields: ['1-2-14-currentBudget'],
    onChange: sumFields,
  },
  {
    // 40. SUB-TOTAL TAXES & INSURANCE
    watchFields: [
      '1-3-0-proposedBudget',
      '1-3-1-proposedBudget',
      '1-3-2-proposedBudget',
      '1-3-3-proposedBudget',
      '1-3-4-proposedBudget',
      '1-3-5-proposedBudget',
    ],
    destinationField: '1-3-6-proposedBudget',
    disableFields: ['1-3-6-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '1-3-0-actual',
      '1-3-1-actual',
      '1-3-2-actual',
      '1-3-3-actual',
      '1-3-4-actual',
      '1-3-5-actual',
    ],
    destinationField: '1-3-6-actual',
    disableFields: ['1-3-6-actual'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '1-3-0-currentBudget',
      '1-3-1-currentBudget',
      '1-3-2-currentBudget',
      '1-3-3-currentBudget',
      '1-3-4-currentBudget',
      '1-3-5-currentBudget',
    ],
    destinationField: '1-3-6-currentBudget',
    disableFields: ['1-3-6-currentBudget'],
    onChange: sumFields,
  },
  {
    // 41. TOTAL O&M EXPENSES
    watchFields: [
      '1-0-10-proposedBudget',
      '1-1-6-proposedBudget',
      '1-2-14-proposedBudget',
      '1-3-6-proposedBudget',
    ],
    destinationField: '1-3-7-proposedBudget',
    disableFields: ['1-3-7-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '1-0-10-actual',
      '1-1-6-actual',
      '1-2-14-actual',
      '1-3-6-actual',
    ],
    destinationField: '1-3-7-actual',
    disableFields: ['1-3-7-actual'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '1-0-10-currentBudget',
      '1-1-6-currentBudget',
      '1-2-14-currentBudget',
      '1-3-6-currentBudget',
    ],
    destinationField: '1-3-7-currentBudget',
    disableFields: ['1-3-7-currentBudget'],
    onChange: sumFields,
  },
  // PART III
  {
    // 2. Transfer to reserve
    watchFields: ['0-2-6-proposedBudget'],
    destinationField: '2-0-1-proposedBudget',
    onChange: linkFields,
  },
  {
    // 4. ANNUAL CAPITAL BUDGET
    watchFields: [
      '3-0-0-reserveActual',
      '3-0-1-reserveActual',
      '3-0-2-reserveActual',
      '3-0-3-reserveActual',
      '3-0-4-reserveActual',
      '3-1-0-reserveActual',
      '3-1-1-reserveActual',
      '3-1-2-reserveActual',
      '3-1-3-reserveActual',
      '3-1-4-reserveActual',
      '3-2-0-reserveActual',
      '3-2-1-reserveActual',
      '3-2-2-reserveActual',
      '3-3-0-reserveActual',
      '3-3-1-reserveActual',
      '3-3-2-reserveActual',
      '3-4-0-reserveActual',
      '3-4-1-reserveActual',
      '3-5-0-reserveActual',
      '3-5-1-reserveActual',
      '3-5-2-reserveActual',
      '3-6-0-reserveActual',
      '3-6-1-reserveActual',
      '3-6-2-reserveActual',
      '3-6-3-reserveActual',
      '3-6-4-reserveActual',
      '3-6-5-reserveActual',
      '3-7-0-reserveActual',
      '3-7-1-reserveActual',
      '3-8-0-reserveActual',
      '3-8-1-reserveActual',
      '3-8-2-reserveActual',
      '3-8-3-reserveActual',
      '3-8-4-reserveActual',
      '3-8-5-reserveActual',
      '3-8-6-reserveActual',
      '3-9-0-reserveActual',
      '3-9-1-reserveActual',
      '3-9-2-reserveActual',
      '3-9-3-reserveActual',
      '3-10-0-reserveActual',
      '3-10-1-reserveActual',
      '3-10-2-reserveActual',
      '3-10-3-reserveActual',
      '3-10-4-reserveActual',
      '3-10-5-reserveActual',
      '3-11-0-reserveActual',
      '3-11-1-reserveActual',
      '3-12-0-reserveActual',
      '3-12-1-reserveActual',
      '3-12-2-reserveActual',
      '3-13-0-reserveActual',
      '3-13-1-reserveActual',
      '3-13-2-reserveActual',
    ],
    destinationField: '2-0-4-actual',
    disableFields: ['2-0-4-actual'],
    onChange: sumFields,
  },
  // PART III
  {
    // 4. ANNUAL CAPITAL BUDGET
    watchFields: [
      '3-0-0-reserveProposed',
      '3-0-1-reserveProposed',
      '3-0-2-reserveProposed',
      '3-0-3-reserveProposed',
      '3-0-4-reserveProposed',
      '3-1-0-reserveProposed',
      '3-1-1-reserveProposed',
      '3-1-2-reserveProposed',
      '3-1-3-reserveProposed',
      '3-1-4-reserveProposed',
      '3-2-0-reserveProposed',
      '3-2-1-reserveProposed',
      '3-2-2-reserveProposed',
      '3-3-0-reserveProposed',
      '3-3-1-reserveProposed',
      '3-3-2-reserveProposed',
      '3-4-0-reserveProposed',
      '3-4-1-reserveProposed',
      '3-5-0-reserveProposed',
      '3-5-1-reserveProposed',
      '3-5-2-reserveProposed',
      '3-6-0-reserveProposed',
      '3-6-1-reserveProposed',
      '3-6-2-reserveProposed',
      '3-6-3-reserveProposed',
      '3-6-4-reserveProposed',
      '3-6-5-reserveProposed',
      '3-7-0-reserveProposed',
      '3-7-1-reserveProposed',
      '3-8-0-reserveProposed',
      '3-8-1-reserveProposed',
      '3-8-2-reserveProposed',
      '3-8-3-reserveProposed',
      '3-8-4-reserveProposed',
      '3-8-5-reserveProposed',
      '3-8-6-reserveProposed',
      '3-9-0-reserveProposed',
      '3-9-1-reserveProposed',
      '3-9-2-reserveProposed',
      '3-9-3-reserveProposed',
      '3-10-0-reserveProposed',
      '3-10-1-reserveProposed',
      '3-10-2-reserveProposed',
      '3-10-3-reserveProposed',
      '3-10-4-reserveProposed',
      '3-10-5-reserveProposed',
      '3-11-0-reserveProposed',
      '3-11-1-reserveProposed',
      '3-12-0-reserveProposed',
      '3-12-1-reserveProposed',
      '3-12-2-reserveProposed',
      '3-13-0-reserveProposed',
      '3-13-1-reserveProposed',
      '3-13-2-reserveProposed',
    ],
    destinationField: '2-0-4-proposedBudget',
    disableFields: ['2-0-4-proposedBudget'],
    onChange: sumFields,
  },
  // PART III
  {
    watchFields: [
      '2-bottom-2-Reserve Account Required Balance',
      '2-0-8-proposedBudget',
    ],
    destinationField: '2-bottom-3-Amount Ahead / Behind',
    disableFields: ['2-bottom-3-Amount Ahead / Behind'],
    operator: '-',
    onChange: calculateFields,
  },
  {
    // 7. TOTAL
    watchFields: [
      '2-0-3-proposedBudget',
      '2-0-4-proposedBudget',
      '2-0-5-proposedBudget',
      '2-0-6-proposedBudget',
    ],
    destinationField: '2-0-7-proposedBudget',
    disableFields: ['2-0-7-proposedBudget'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '2-0-3-actual',
      '2-0-4-actual',
      '2-0-5-actual',
      '2-0-6-actual',
    ],
    destinationField: '2-0-7-actual',
    disableFields: ['2-0-7-actual'],
    onChange: sumFields,
  },
  {
    watchFields: [
      '2-0-3-currentBudget',
      '2-0-4-currentBudget',
      '2-0-5-currentBudget',
      '2-0-6-currentBudget',
    ],
    destinationField: '2-0-7-currentBudget',
    disableFields: ['2-0-7-currentBudget'],
    onChange: sumFields,
  },
  {
    // 8. ENDING BALANCE - Part III - proposedBudget - Locked calculation
    watchFields: [
      '2-0-0-proposedBudget',
      '2-0-1-proposedBudget',
      '2-0-7-proposedBudget',
    ],
    destinationField: '2-0-8-proposedBudget',
    disableFields: ['2-0-8-proposedBudget'],
    onChange: calculateEndingBalance,
  },
  {
    // 8. ENDING BALANCE - Part III - currentBudget - Locked calculation
    watchFields: [
      '2-0-0-currentBudget',
      '2-0-1-currentBudget',
      '2-0-7-currentBudget',
    ],
    destinationField: '2-0-8-currentBudget',
    disableFields: ['2-0-8-currentBudget'],
    onChange: calculateEndingBalance,
  },
  {
    // 8. ENDING BALANCE - Part III - actual - Locked calculation
    watchFields: ['2-0-0-actual', '2-0-1-actual', '2-0-7-actual'],
    destinationField: '2-0-8-actual',
    disableFields: ['2-0-8-actual'],
    totalAbs: true,
    onChange: calculateEndingBalance,
  },
  // PART V
  ...expenseCalculations(),
  ...createReserveProposed(),
  // // Total Cost Actual = Reserved Actual + Operating Actual
  ...allFields
    .filter((f) => f.pageIndex === 3 && f.key === 'totalActual')
    .reduce((acc, totalActualFieldConfig) => {
      const totalActualFieldName = totalActualFieldConfig.name;
      const reserveActualFieldName = totalActualFieldName.replace(
        'totalActual',
        'reserveActual',
      );
      const operatingActualFieldName = totalActualFieldName.replace(
        'totalActual',
        'operatingActual',
      );
      acc.push({
        watchFields: [reserveActualFieldName, operatingActualFieldName],
        destinationField: totalActualFieldName,
        disableFields: [totalActualFieldName],
        onChange: sumFields,
      });
      return acc;
    }, []),
  // // Units/Items Total Actual = (Reserved Actual + Operating Actual) ÷ Price
  ...allFields
    .filter((f) => f.pageIndex === 3 && f.key === 'unitsActual')
    .reduce((acc, unitsActualFieldConfig) => {
      const unitsActualFieldName = unitsActualFieldConfig.name;
      const reserveActualFieldName = unitsActualFieldName.replace(
        'unitsActual',
        'reserveActual',
      );
      const operatingActualFieldName = unitsActualFieldName.replace(
        'unitsActual',
        'operatingActual',
      );
      const priceFieldName = unitsActualFieldName.replace(
        'unitsActual',
        'priceCurrent',
      );
      acc.push({
        watchFields: [
          reserveActualFieldName,
          operatingActualFieldName,
          priceFieldName,
        ],
        destinationField: unitsActualFieldName,
        disableFields: [unitsActualFieldName],
        onChange: calculateP5TotalActual,
      });
      return acc;
    }, []),
  {
    // Vacancy calculation - Line 8
    watchFields: ['0-0-0-proposedBudget', '0-0-7-comments'],
    disableFields: ['0-0-7-proposedBudget'],
    destinationField: '0-0-7-proposedBudget',
    onChange: calculateVacancy,
  },
];

// List of all fields that should be ignored for intacct difference. Only disabled fields
export const ignoreDifferenceFields = new Set(
  fieldCalculations
    .filter((configuration) => {
      return configuration.disableFields;
    })
    .reduce((acc, configuration) => {
      return acc.concat(configuration.disableFields);
    }, []),
);
